import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ErrorMessage } from '@/components/ui/error-message';
import { authApi } from '@/api/auth';
import { toast } from '@/components/ui/use-toast';
import Routes from '@/router/routes';
import TelletLogo from '@/assets/logo/tellet-logo-main.svg';
import { passwordSchema, confirmPasswordSchema } from '@/lib/validationSchemas';

const validationSchema = Yup.object().shape({
  newPassword: passwordSchema,
  confirmPassword: confirmPasswordSchema('newPassword'),
});

type FormData = Yup.InferType<typeof validationSchema>;

const defaultValues: FormData = {
  newPassword: '',
  confirmPassword: '',
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiError, setApiError] = useState('');
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  useEffect(() => {
    if (!token) {
      toast({
        title: 'Invalid link',
        description: 'The password reset link is invalid or missing.',
        variant: 'destructive',
      });
      navigate(Routes.login.path);
    }
  }, [token, navigate]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const onFormSubmit = async (data: FormData) => {
    if (!token) return;

    setIsLoading(true);
    setApiError('');

    try {
      await authApi.resetPassword(token, data.newPassword);
      setIsSuccess(true);
      toast({
        title: 'Password reset successful',
        description: 'Your password has been updated. Redirecting to login...',
      });

      redirectTimerRef.current = setTimeout(() => {
        navigate(Routes.login.path);
      }, 3000);
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to reset password. The link may be invalid or expired.',
        variant: 'destructive',
      });
      setApiError('Failed to reset password. Please try again or request a new reset link.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <img src={TelletLogo} alt="Tellet" className="mx-auto h-12 w-auto dark:invert" />
            <div className="mt-6 mx-auto h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Check size={32} className="text-green-600 dark:text-green-400" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-foreground">Password reset successful</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Your password has been updated. You can now sign in with your new password.
            </p>
          </div>

          <div className="rounded-md bg-green-50 dark:bg-green-900/30 p-4">
            <p className="text-sm text-green-700 dark:text-green-400">Redirecting to login page...</p>
          </div>

          <Button onClick={() => navigate(Routes.login.path)} className="w-full">
            Go to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src={TelletLogo} alt="Tellet" className="mx-auto h-12 w-auto dark:invert" />
          <h2 className="mt-6 text-3xl font-bold text-foreground">Reset your password</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Choose a new password for your account. Make sure it's at least 8 characters long.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onFormSubmit)}>
          <div className="space-y-4">
            <div>
              <label htmlFor="new-password" className="block text-sm font-medium text-foreground mb-1">
                New password
              </label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                placeholder="Enter new password (min 8 characters)"
                {...register('newPassword')}
                icon={<Lock size={20} />}
                hasError={!!errors.newPassword}
              />
              <ErrorMessage message={errors.newPassword?.message} />
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Confirm password
              </label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                placeholder="Confirm your new password"
                {...register('confirmPassword')}
                icon={<Lock size={20} />}
                hasError={!!errors.confirmPassword}
              />
              <ErrorMessage message={errors.confirmPassword?.message} />
            </div>
          </div>

          {apiError && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
              <p className="text-sm text-red-700 dark:text-red-400">{apiError}</p>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Resetting password...' : 'Reset password'}
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => navigate(Routes.login.path)}
          >
            Back to login
          </Button>
        </form>
      </div>
    </div>
  );
}
